import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Search, Download, X, BookUser, Plus, Trash2, Save } from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface FihristKategori {
  kategoriID: number;
  kategoriAdi: string;
  aktifMi: boolean;
}

interface FihristDetay {
  detayID?: number;
  tur: string;
  deger: string;
  etiket?: string;
}

interface FihristKayit {
  kayitID: number;
  firmaAdi?: string;
  adSoyad?: string;
  adres?: string;
  kategoriID?: number;
  notlar?: string;
  aktifMi: boolean;
  eklenmeTarihi: string;
  kategori?: FihristKategori;
  detaylar: FihristDetay[];
}

interface KayitForm {
  firmaAdi: string;
  adSoyad: string;
  adres: string;
  kategoriID: number | '';
  notlar: string;
  detaylar: FihristDetay[];
}

const turOptions = ['Telefon', 'Cep', 'E-posta', 'İlgili Kişi'];
const etiketOptions = ['İş', 'Şahsi', 'Muhasebe Birimi', 'Santral', 'Faks'];

const emptyForm: KayitForm = { firmaAdi: '', adSoyad: '', adres: '', kategoriID: '', notlar: '', detaylar: [] };

const Fihrist: React.FC = () => {
  const [kategoriler, setKategoriler] = useState<FihristKategori[]>([]);
  const [kayitlar, setKayitlar] = useState<FihristKayit[]>([]);
  const [seciliKategori, setSeciliKategori] = useState<number | null>(null);
  const [arama, setArama] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editKayit, setEditKayit] = useState<FihristKayit | null>(null);
  const [form, setForm] = useState<KayitForm>({ ...emptyForm });
  const [showDetayModal, setShowDetayModal] = useState(false);
  const [detayKayit, setDetayKayit] = useState<FihristKayit | null>(null);
  const [showKatModal, setShowKatModal] = useState(false);
  const [katForm, setKatForm] = useState({ kategoriAdi: '', aktifMi: true });
  const [editKat, setEditKat] = useState<FihristKategori | null>(null);
  const [showKatListModal, setShowKatListModal] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  const fetchKategoriler = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/FihristKategorileri');
      setKategoriler(res.data);
    } catch { toast.error('Kategoriler yüklenemedi'); }
  }, []);

  const fetchKayitlar = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (seciliKategori) params.kategoriId = seciliKategori;
      if (arama.trim()) params.arama = arama.trim();
      const res = await axiosInstance.get('/api/FihristKayitlari', { params });
      setKayitlar(res.data);
      setCurrentPage(1);
    } catch { toast.error('Kayıtlar yüklenemedi'); }
    setLoading(false);
  }, [seciliKategori, arama]);

  useEffect(() => { fetchKategoriler(); }, [fetchKategoriler]);
  useEffect(() => { fetchKayitlar(); }, [fetchKayitlar]);

  const openAddModal = () => {
    setEditKayit(null);
    setForm({ ...emptyForm, detaylar: [{ tur: 'Cep', deger: '', etiket: '' }] });
    setShowModal(true);
  };

  const openEditModal = (k: FihristKayit) => {
    setEditKayit(k);
    setForm({
      firmaAdi: k.firmaAdi || '',
      adSoyad: k.adSoyad || '',
      adres: k.adres || '',
      kategoriID: k.kategoriID || '',
      notlar: k.notlar || '',
      detaylar: k.detaylar.length > 0 ? k.detaylar.map(d => ({ ...d })) : [{ tur: 'Cep', deger: '', etiket: '' }]
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.firmaAdi && !form.adSoyad) { toast.warn('Firma adı veya ad soyad zorunludur.'); return; }
    const validDetaylar = form.detaylar.filter(d => d.deger.trim()).map(d => ({
      tur: d.tur, deger: d.deger, etiket: d.etiket || null
    }));
    const payload = {
      firmaAdi: form.firmaAdi || null,
      adSoyad: form.adSoyad || null,
      adres: form.adres || null,
      kategoriID: form.kategoriID || null,
      notlar: form.notlar || null,
      aktifMi: true,
      detaylar: validDetaylar
    };
    try {
      if (editKayit) {
        await axiosInstance.put(`/api/FihristKayitlari/${editKayit.kayitID}`, payload);
        toast.success('Kayıt güncellendi');
      } else {
        await axiosInstance.post('/api/FihristKayitlari', payload);
        toast.success('Kayıt eklendi');
      }
      setShowModal(false);
      fetchKayitlar();
    } catch { toast.error('Kayıt kaydedilemedi'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu kayıdı silmek istediğinize emin misiniz?')) return;
    try {
      await axiosInstance.delete(`/api/FihristKayitlari/${id}`);
      toast.success('Kayıt silindi');
      fetchKayitlar();
    } catch { toast.error('Silinemedi'); }
  };

  const addDetaySatiri = () => setForm(f => ({ ...f, detaylar: [...f.detaylar, { tur: 'Cep', deger: '', etiket: '' }] }));
  const removeDetaySatiri = (i: number) => setForm(f => ({ ...f, detaylar: f.detaylar.filter((_, idx) => idx !== i) }));
  const updateDetay = (i: number, field: keyof FihristDetay, val: string) => {
    setForm(f => {
      const arr = [...f.detaylar];
      arr[i] = { ...arr[i], [field]: val };
      return { ...f, detaylar: arr };
    });
  };

  const openAddKat = () => { setEditKat(null); setKatForm({ kategoriAdi: '', aktifMi: true }); setShowKatModal(true); };
  const openEditKat = (k: FihristKategori) => { setEditKat(k); setKatForm({ kategoriAdi: k.kategoriAdi, aktifMi: k.aktifMi }); setShowKatModal(true); };

  const handleSaveKat = async () => {
    if (!katForm.kategoriAdi.trim()) { toast.warn('Kategori adı zorunludur.'); return; }
    try {
      if (editKat) {
        await axiosInstance.put(`/api/FihristKategorileri/${editKat.kategoriID}`, katForm);
        toast.success('Kategori güncellendi');
      } else {
        await axiosInstance.post('/api/FihristKategorileri', katForm);
        toast.success('Kategori eklendi');
      }
      setShowKatModal(false);
      fetchKategoriler();
    } catch { toast.error('Kategori kaydedilemedi'); }
  };

  const handleDeleteKat = async (id: number) => {
    if (!window.confirm('Kategoriyi silmek istediğinize emin misiniz?')) return;
    try {
      await axiosInstance.delete(`/api/FihristKategorileri/${id}`);
      toast.success('Kategori silindi');
      fetchKategoriler();
      if (seciliKategori === id) setSeciliKategori(null);
    } catch { toast.error('Silinemedi'); }
  };

  const katBadgeStyle = (kategoriAdi: string): React.CSSProperties => {
    const base: React.CSSProperties = {
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 10px', borderRadius: 6,
      fontSize: 11, fontWeight: 500,
    };
    switch (kategoriAdi) {
      case 'Müşteri':   return { ...base, background: '#eff6ff', color: '#1d4ed8' };
      case 'Taşeron':   return { ...base, background: '#fffbeb', color: '#b45309' };
      case 'Tedarikçi': return { ...base, background: '#faf5ff', color: '#7e22ce' };
      case 'Personel':  return { ...base, background: '#ecfdf5', color: '#047857' };
      default:          return { ...base, background: 'rgba(205,105,6,0.08)', color: '#cd6906' };
    }
  };

  const turIcon = (tur: string) => {
    switch (tur) {
      case 'Telefon': return 'fas fa-phone';
      case 'Cep': return 'fas fa-mobile-alt';
      case 'E-posta': return 'fas fa-envelope';
      case 'İlgili Kişi': return 'fas fa-user';
      default: return 'fas fa-info-circle';
    }
  };

  const handleFihristPdfExport = async () => {
    setShowExportMenu(false);
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-9999px;top:0;width:1100px;font-family:Arial,sans-serif;background:#fff;padding:24px;box-sizing:border-box;';

    const rows = kayitlar.map((k, i) => {
      const iletisim = k.detaylar.map(d => `${d.tur}: ${d.deger}${d.etiket ? ` (${d.etiket})` : ''}`).join('<br>');
      return `
        <tr style="background:${i % 2 === 0 ? '#ffffff' : '#f0f3f8'};">
          <td style="padding:7px 8px;border:1px solid #e0e0e0;font-size:11px;">${k.adSoyad || '—'}</td>
          <td style="padding:7px 8px;border:1px solid #e0e0e0;font-size:11px;">${k.firmaAdi || '—'}</td>
          <td style="padding:7px 8px;border:1px solid #e0e0e0;font-size:11px;">${k.kategori?.kategoriAdi || '—'}</td>
          <td style="padding:7px 8px;border:1px solid #e0e0e0;font-size:11px;line-height:1.7;">${iletisim || '—'}</td>
          <td style="padding:7px 8px;border:1px solid #e0e0e0;font-size:11px;">${k.adres || '—'}</td>
          <td style="padding:7px 8px;border:1px solid #e0e0e0;font-size:11px;">${k.notlar || '—'}</td>
        </tr>`;
    }).join('');

    container.innerHTML = `
      <div style="margin-bottom:18px;border-bottom:3px solid #e67d21;padding-bottom:12px;">
        <h2 style="color:#1a2440;font-size:22px;margin:0 0 6px 0;font-weight:bold;">Fihrist</h2>
        <p style="margin:2px 0;color:#555;font-size:12px;">Toplam <strong>${kayitlar.length}</strong> kayıt &nbsp;&bull;&nbsp; ${new Date().toLocaleDateString('tr-TR')}</p>
      </div>
      <table style="width:100%;border-collapse:collapse;">
        <thead>
          <tr style="background:#1a2440;color:#fff;">
            <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">AD SOYAD</th>
            <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">FİRMA</th>
            <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">KATEGORİ</th>
            <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">İLETİŞİM</th>
            <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">ADRES</th>
            <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">NOTLAR</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="color:#aaa;font-size:10px;text-align:right;margin:8px 0 0 0;">YapiCRM &bull; ${new Date().toLocaleDateString('tr-TR')}</p>
    `;

    document.body.appendChild(container);
    try {
      const canvas = await html2canvas(container, { scale: 2, useCORS: true, logging: false });
      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 8;
      const imgW = pageW - 2 * margin;
      const imgH = (canvas.height * imgW) / canvas.width;
      const pageContentH = pageH - 2 * margin;
      let offset = 0;
      let page = 0;
      while (offset < imgH) {
        if (page > 0) doc.addPage();
        doc.addImage(imgData, 'PNG', margin, margin - offset, imgW, imgH);
        offset += pageContentH;
        page++;
      }
      doc.save('fihrist.pdf');
      toast.success('PDF başarıyla indirildi');
    } finally {
      document.body.removeChild(container);
    }
  };

  const handleFihristXlsExport = () => {
    setShowExportMenu(false);
    const wsData = [
      ['Ad Soyad', 'Firma Adı', 'Kategori', 'İletişim Bilgileri', 'Adres', 'Notlar', 'Eklenme Tarihi'],
      ...kayitlar.map(k => [
        k.adSoyad || '',
        k.firmaAdi || '',
        k.kategori?.kategoriAdi || '',
        k.detaylar.map(d => `${d.tur}: ${d.deger}${d.etiket ? ` (${d.etiket})` : ''}`).join('; '),
        k.adres || '',
        k.notlar || '',
        new Date(k.eklenmeTarihi).toLocaleDateString('tr-TR')
      ])
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 22 }, { wch: 25 }, { wch: 15 }, { wch: 45 }, { wch: 30 }, { wch: 30 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fihrist');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'fihrist.xlsx');
    toast.success('Excel başarıyla indirildi');
  };

  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '24px', minHeight: 'calc(100vh - 80px)', background: 'transparent', color: '#1a1a1a' },
    topBar: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' as const, alignItems: 'center' },
    categorySelect: { minWidth: 200, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#1a1a1a' },
    searchInput: { flex: 1, minWidth: 220, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#1a1a1a' },
    addBtn: { background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
    manageKatBtn: { background: '#fff', color: '#cd6906', border: '1px solid #cd6906', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
    tableContainer: { background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse' as const },
    th: { background: '#f9fafb', padding: '12px 16px', textAlign: 'left' as const, fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' as const },
    td: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: 14, color: '#444' },
    tdPrimary: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: 14, fontWeight: 500, color: '#111827' },
    tdSecondary: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: 13, color: '#6b7280', fontVariantNumeric: 'tabular-nums' as const },
    badge: { display: 'inline-block', background: 'rgba(205,105,6,0.1)', color: '#cd6906', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20 },
    detayRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' },
    detayIcon: { width: 18, textAlign: 'center' as const, color: '#999' },
    detayEtiket: { fontSize: 11, color: '#999', marginLeft: 4 },
    actBtn: { padding: '6px 10px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer', textAlign: 'center' as const, background: 'transparent', display: 'inline-flex', alignItems: 'center', gap: 5, marginRight: 4 },
    overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1050, display: 'flex', justifyContent: 'center', alignItems: 'center' },
    modal: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 620, maxHeight: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' as const, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb' },
    modalHeader: { padding: '16px 20px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 17, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#1a1a1a' },
    modalBody: { padding: 20, overflowY: 'auto' as const, flex: 1 },
    modalFooter: { padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 },
    formGroup: { marginBottom: 14 },
    label: { display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4, color: '#444' },
    input: { width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#1a1a1a' },
    select: { width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#1a1a1a' },
    textarea: { width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical' as const, minHeight: 60, background: '#fff', color: '#1a1a1a' },
    detayTitle: { fontWeight: 700, fontSize: 14, marginBottom: 8, marginTop: 8, color: '#1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    detayInputRow: { display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' },
    smallSelect: { width: 120, padding: '7px 8px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff', color: '#1a1a1a' },
    smallInput: { flex: 1, padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff', color: '#1a1a1a' },
    removeBtn: { background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 },
    addDetayBtn: { background: 'rgba(205,105,6,0.06)', color: '#cd6906', border: '1px dashed #cd6906', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%' },
    empty: { textAlign: 'center' as const, padding: 60, color: '#999', fontSize: 15 },
  };

  return (
    <div style={styles.page}>
      {/* Sayfa Başlığı */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32, gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', letterSpacing: '-0.3px', margin: 0 }}>Fihrist</h1>
          <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Müşteri, tedarikçi, taşeron ve personellerinizin iletişim bilgilerini yönetin.</p>
        </div>
        <button style={styles.addBtn} onClick={openAddModal}>
          <i className="fas fa-plus" /> Yeni Kayıt
        </button>
      </div>

      {/* Araç Çubuğu */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' as const, alignItems: 'center' }}>
        {/* Arama */}
        <div style={{ position: 'relative' as const, flex: 1, minWidth: 220 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
          <input
            style={{ ...styles.searchInput, paddingLeft: 36 }}
            placeholder="İsim, firma veya telefon ile ara..."
            value={arama}
            onChange={e => setArama(e.target.value)}
          />
        </div>
        {/* Kategori Filtresi */}
        <select
          style={styles.categorySelect}
          value={seciliKategori || ''}
          onChange={e => setSeciliKategori(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Tüm Kategoriler</option>
          {kategoriler.filter(k => k.aktifMi).map(k => (
            <option key={k.kategoriID} value={k.kategoriID}>{k.kategoriAdi}</option>
          ))}
        </select>
        {/* Kategoriler Yönetim */}
        <button style={styles.manageKatBtn} onClick={() => setShowKatListModal(true)}>
          <i className="fas fa-tags" /> Kategoriler
        </button>
        {/* Dışa Aktar */}
        <div style={{ position: 'relative' }}>
          <button
            style={{ background: '#fff', border: '1px solid #e5e7eb', color: '#374151', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
            onClick={() => setShowExportMenu(m => !m)}
          >
            <Download size={14} /> Dışa Aktar
          </button>
          {showExportMenu && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setShowExportMenu(false)} />
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, minWidth: 160, overflow: 'hidden' }}>
                <button
                  onClick={handleFihristPdfExport}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '11px 16px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#374151', textAlign: 'left' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >
                  <i className="fas fa-file-pdf" style={{ color: '#e67d21' }} /> PDF İndir
                </button>
                <button
                  onClick={handleFihristXlsExport}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '11px 16px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#374151', textAlign: 'left', borderTop: '1px solid #f0f0f0' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >
                  <i className="fas fa-file-excel" style={{ color: '#1a7340' }} /> Excel İndir
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div style={styles.empty}><i className="fas fa-spinner fa-spin" style={{ color: '#cd6906' }} /> Yükleniyor...</div>
      ) : kayitlar.length === 0 ? (
        <div style={styles.empty}>
          <i className="fas fa-address-book" style={{ fontSize: 40, marginBottom: 12, display: 'block', color: '#ccc' }} />
          Kayıt bulunamadı.
        </div>
      ) : (
        <>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Ad Soyad</th>
                <th style={styles.th}>Firma</th>
                <th style={styles.th}>Kategori</th>
                <th style={styles.th}>İletişim Bilgileri</th>
                <th style={styles.th}>Adres</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {kayitlar.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map(k => (
                <tr key={k.kayitID} style={{ background: '#fff' }}>
                  <td style={styles.tdPrimary}>{k.adSoyad || '—'}</td>
                  <td style={styles.tdPrimary}>{k.firmaAdi || '—'}</td>
                  <td style={styles.td}>
                    {k.kategori ? <span style={katBadgeStyle(k.kategori.kategoriAdi)}>{k.kategori.kategoriAdi}</span> : '—'}
                  </td>
                  <td style={styles.tdSecondary}>
                    {k.detaylar.length === 0 ? (
                      <span style={{ color: '#d1d5db' }}>—</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {k.detaylar.slice(0, 3).map((d, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <i className={turIcon(d.tur)} style={{ color: '#9ca3af', width: 14, flexShrink: 0 }} />
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{d.deger}</span>
                            {d.etiket && <span style={{ fontSize: 11, color: '#9ca3af' }}>({d.etiket})</span>}
                          </div>
                        ))}
                        {k.detaylar.length > 3 && (
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>+{k.detaylar.length - 3} daha...</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={styles.tdSecondary}>
                    {k.adres ? <div style={{ maxWidth: 200 }}>{k.adres}</div> : <span style={{ color: '#d1d5db' }}>—</span>}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button
                      style={{ ...styles.actBtn, color: hoveredBtn === `detay-${k.kayitID}` ? '#cd6906' : '#888' }}
                      onClick={() => { setDetayKayit(k); setShowDetayModal(true); }}
                      onMouseEnter={() => setHoveredBtn(`detay-${k.kayitID}`)}
                      onMouseLeave={() => setHoveredBtn(null)}
                      title="Detay"
                    ><i className="fas fa-eye" /></button>
                    <button
                      style={{ ...styles.actBtn, color: hoveredBtn === `edit-${k.kayitID}` ? '#cd6906' : '#888' }}
                      onClick={() => openEditModal(k)}
                      onMouseEnter={() => setHoveredBtn(`edit-${k.kayitID}`)}
                      onMouseLeave={() => setHoveredBtn(null)}
                      title="Düzenle"
                    ><i className="fas fa-pen" /></button>
                    <button
                      style={{ ...styles.actBtn, color: hoveredBtn === `del-${k.kayitID}` ? '#dc2626' : '#888' }}
                      onClick={() => handleDelete(k.kayitID)}
                      onMouseEnter={() => setHoveredBtn(`del-${k.kayitID}`)}
                      onMouseLeave={() => setHoveredBtn(null)}
                      title="Sil"
                    ><i className="fas fa-trash-alt" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Sayfalama */}
        {kayitlar.length > PAGE_SIZE && (() => {
          const totalPages = Math.ceil(kayitlar.length / PAGE_SIZE);
          const pages: (number | '...')[] = [];
          for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - 2 && i <= currentPage + 2)) {
              pages.push(i);
            } else if (pages[pages.length - 1] !== '...') {
              pages.push('...');
            }
          }
          const btnBase: React.CSSProperties = {
            minWidth: 34, height: 34, padding: '0 10px', border: '1px solid #e5e7eb',
            borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer',
            background: '#fff', color: '#374151', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
          };
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 13, color: '#6b7280' }}>
                {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, kayitlar.length)} / {kayitlar.length} kayıt
              </span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button
                  style={{ ...btnBase, opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >‹</button>
                {pages.map((p, i) =>
                  p === '...' ? (
                    <span key={`e${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: 13 }}>…</span>
                  ) : (
                    <button
                      key={p}
                      style={{ ...btnBase, background: currentPage === p ? '#1a2440' : '#fff', color: currentPage === p ? '#fff' : '#374151', borderColor: currentPage === p ? '#1a2440' : '#e5e7eb', fontWeight: currentPage === p ? 700 : 500 }}
                      onClick={() => setCurrentPage(p as number)}
                    >{p}</button>
                  )
                )}
                <button
                  style={{ ...btnBase, opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >›</button>
              </div>
            </div>
          );
        })()}
        </>
      )}

      {showModal && (() => {
        const inp: React.CSSProperties = {
          width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
          borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff',
          color: '#111827', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s',
        };
        const lbl: React.CSSProperties = { display: 'block', marginBottom: 5, fontWeight: 500, color: '#374151', fontSize: 12 };
        const onFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
          e.currentTarget.style.borderColor = '#111827';
          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)';
        };
        const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
          e.currentTarget.style.borderColor = '#d1d5db';
          e.currentTarget.style.boxShadow = 'none';
        };
        return (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={() => setShowModal(false)}
          >
            <div
              style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 672, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', fontFamily: "'Inter','Segoe UI',sans-serif" }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <h4 style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: 18 }}>
                      {editKayit ? 'Kayıt Düzenle' : 'Yeni Fihrist Kaydı'}
                    </h4>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                      Müşteri, tedarikçi veya personel iletişim bilgilerini kaydedin.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#374151'; e.currentTarget.style.background = '#f3f4f6'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.background = 'none'; }}
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20, flex: 1 }}>

                {/* Ad Soyad + Firma */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={lbl}>Ad Soyad</label>
                    <input style={inp} value={form.adSoyad} onChange={e => setForm(f => ({ ...f, adSoyad: e.target.value }))} placeholder="Ad ve soyad girin" onFocus={onFocus} onBlur={onBlur} />
                  </div>
                  <div>
                    <label style={lbl}>Firma Adı</label>
                    <input style={inp} value={form.firmaAdi} onChange={e => setForm(f => ({ ...f, firmaAdi: e.target.value }))} placeholder="Firma adı girin" onFocus={onFocus} onBlur={onBlur} />
                  </div>
                </div>

                {/* Kategori + Adres */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={lbl}>Kategori</label>
                    <select style={inp} value={form.kategoriID} onChange={e => setForm(f => ({ ...f, kategoriID: e.target.value ? Number(e.target.value) : '' }))} onFocus={onFocus} onBlur={onBlur}>
                      <option value="">Seçiniz</option>
                      {kategoriler.filter(k => k.aktifMi).map(k => (
                        <option key={k.kategoriID} value={k.kategoriID}>{k.kategoriAdi}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Adres</label>
                    <input style={inp} value={form.adres} onChange={e => setForm(f => ({ ...f, adres: e.target.value }))} placeholder="Adres bilgisi girin" onFocus={onFocus} onBlur={onBlur} />
                  </div>
                </div>

                {/* Notlar */}
                <div>
                  <label style={lbl}>Notlar</label>
                  <textarea
                    style={{ ...inp, minHeight: 80, resize: 'vertical' } as React.CSSProperties}
                    value={form.notlar}
                    onChange={e => setForm(f => ({ ...f, notlar: e.target.value }))}
                    placeholder="Ek notlar..."
                    onFocus={onFocus as any}
                    onBlur={onBlur as any}
                  />
                </div>

                {/* İletişim Bilgileri */}
                <div style={{ background: 'rgba(249,250,251,0.7)', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <BookUser size={15} color="#cd6906" />
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>İletişim Bilgileri</span>
                      <span style={{ background: '#f3f4f6', color: '#6b7280', borderRadius: 20, padding: '1px 9px', fontSize: 11, fontWeight: 500 }}>{form.detaylar.length}</span>
                    </div>
                    <button
                      type="button"
                      onClick={addDetaySatiri}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, fontWeight: 500, color: '#374151', cursor: 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.borderColor = '#9ca3af'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#d1d5db'; }}
                    >
                      <Plus size={13} /> Satır Ekle
                    </button>
                  </div>

                  {form.detaylar.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 13 }}>Henüz iletişim bilgisi eklenmedi.</div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {form.detaylar.map((d, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 120px 36px', gap: 8, alignItems: 'center' }}>
                        <select
                          style={{ ...inp, padding: '8px 10px' }}
                          value={d.tur}
                          onChange={e => updateDetay(i, 'tur', e.target.value)}
                          onFocus={onFocus}
                          onBlur={onBlur}
                        >
                          {turOptions.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <input
                          style={{ ...inp, padding: '8px 12px' }}
                          placeholder={d.tur === 'E-posta' ? 'ornek@mail.com' : d.tur === 'İlgili Kişi' ? 'Ad Soyad' : '05XX XXX XX XX'}
                          value={d.deger}
                          onChange={e => updateDetay(i, 'deger', e.target.value)}
                          onFocus={onFocus}
                          onBlur={onBlur}
                        />
                        <select
                          style={{ ...inp, padding: '8px 10px' }}
                          value={d.etiket || ''}
                          onChange={e => updateDetay(i, 'etiket', e.target.value)}
                          onFocus={onFocus}
                          onBlur={onBlur}
                        >
                          <option value="">Etiket</option>
                          {etiketOptions.map(et => <option key={et} value={et}>{et}</option>)}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeDetaySatiri(i)}
                          style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', color: '#9ca3af', transition: 'all 0.15s', flexShrink: 0 }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ padding: '16px 28px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 500, color: '#374151', cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >İptal</button>
                <button
                  type="button"
                  onClick={handleSave}
                  style={{ padding: '9px 22px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                >
                  <Save size={13} /> {editKayit ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showDetayModal && detayKayit && (
        <div style={styles.overlay} onClick={() => setShowDetayModal(false)}>
          <div style={{ ...styles.modal, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span>{detayKayit.adSoyad || detayKayit.firmaAdi || 'Kayıt Detayı'}</span>
              <button onClick={() => setShowDetayModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666' }}>&times;</button>
            </div>
            <div style={styles.modalBody}>
              {detayKayit.firmaAdi && detayKayit.adSoyad && (
                <div style={{ marginBottom: 10, fontSize: 14, color: '#666' }}><i className="fas fa-building" style={{ marginRight: 6 }} />{detayKayit.firmaAdi}</div>
              )}
              {detayKayit.kategori && (
                <div style={{ marginBottom: 10 }}><span style={styles.badge}>{detayKayit.kategori.kategoriAdi}</span></div>
              )}
              {detayKayit.adres && (
                <div style={{ ...styles.detayRow, marginBottom: 10 }}><i className="fas fa-map-marker-alt" style={styles.detayIcon} /><span>{detayKayit.adres}</span></div>
              )}
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, marginTop: 12, color: '#1a1a1a' }}>
                <i className="fas fa-address-card" style={{ marginRight: 6 }} />İletişim Bilgileri
              </div>
              {detayKayit.detaylar.length === 0 ? (
                <div style={{ color: '#999', fontSize: 13 }}>İletişim bilgisi yok.</div>
              ) : (
                detayKayit.detaylar.map((d, i) => (
                  <div key={i} style={{ ...styles.detayRow, marginBottom: 8, padding: '8px 12px', background: '#f9fafb', borderRadius: 8 }}>
                    <i className={turIcon(d.tur)} style={{ ...styles.detayIcon, color: '#cd6906' }} />
                    <span style={{ fontWeight: 600, marginRight: 4 }}>{d.tur}:</span>
                    <span>{d.deger}</span>
                    {d.etiket && <span style={{ ...styles.detayEtiket, background: '#f3f4f6', padding: '1px 8px', borderRadius: 10, color: '#888' }}>({d.etiket})</span>}
                  </div>
                ))
              )}
              {detayKayit.notlar && (
                <>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, marginTop: 14, color: '#1a1a1a' }}><i className="fas fa-sticky-note" style={{ marginRight: 6 }} />Notlar</div>
                  <div style={{ fontSize: 13, color: '#555', background: '#fffbeb', padding: 12, borderRadius: 8, whiteSpace: 'pre-wrap' }}>{detayKayit.notlar}</div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showKatListModal && (
        <div style={styles.overlay} onClick={() => setShowKatListModal(false)}>
          <div style={{ ...styles.modal, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span><i className="fas fa-tags" style={{ marginRight: 8 }} />Kategori Yönetimi</span>
              <button onClick={() => setShowKatListModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666' }}>&times;</button>
            </div>
            <div style={{ ...styles.modalBody, maxHeight: '500px' }}>
              <button onClick={() => { openAddKat(); setShowKatListModal(false); }} style={{ ...styles.addBtn, width: '100%', justifyContent: 'center', marginBottom: 16 }}>
                <i className="fas fa-plus" /> Yeni Kategori Ekle
              </button>
              {kategoriler.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Henüz kategori eklenmemiş.</div>
              ) : (
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Kategori Adı</th>
                        <th style={styles.th}>Durum</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kategoriler.map(k => (
                        <tr key={k.kategoriID}>
                          <td style={styles.td}><strong>{k.kategoriAdi}</strong></td>
                          <td style={styles.td}>
                            {k.aktifMi ? (
                              <span style={{ color: '#10b981', fontSize: 13 }}><i className="fas fa-check-circle" style={{ marginRight: 4 }} />Aktif</span>
                            ) : (
                              <span style={{ color: '#999', fontSize: 13 }}><i className="fas fa-times-circle" style={{ marginRight: 4 }} />Pasif</span>
                            )}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                            <button style={{ ...styles.actBtn, color: '#cd6906' }} onClick={() => { openEditKat(k); setShowKatListModal(false); }}>
                              <i className="fas fa-pen" /> Düzenle
                            </button>
                            <button style={{ ...styles.actBtn, color: '#dc2626' }} onClick={() => handleDeleteKat(k.kategoriID)}>
                              <i className="fas fa-trash-alt" /> Sil
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showKatModal && (
        <div style={styles.overlay} onClick={() => setShowKatModal(false)}>
          <div style={{ ...styles.modal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span>{editKat ? 'Kategori Düzenle' : 'Yeni Kategori'}</span>
              <button onClick={() => setShowKatModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666' }}>&times;</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Kategori Adı</label>
                <input style={styles.input} value={katForm.kategoriAdi} onChange={e => setKatForm(f => ({ ...f, kategoriAdi: e.target.value }))} />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={() => setShowKatModal(false)} style={{ padding: '8px 20px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#555' }}>İptal</button>
              <button onClick={handleSaveKat} style={{ padding: '8px 20px', border: 'none', borderRadius: 8, background: '#cd6906', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                {editKat ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fihrist;
